import { yupResolver } from "@hookform/resolvers/yup";
import { drop } from "lodash";
import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { array, object, SchemaOf, string } from "yup";
import { useAppSelector } from "../../../../../../../app/hooks";
import { LSearchSelect } from "../../../../../../../common/components/LSearchSelect";
import { DefaultErrorMessages } from "../../../../../../../common/helpers/formValidationHelper";
import { InviteUser, InviteUserArray, RoleId } from "@store/generalTypes";
import { useModal } from "../../../../../../../services/ModalContext";
import { useApi } from "../../../../../../../services/useApi";
import styles from "./AssignStaffUserModal.module.css";
import { Invitees } from "./Invitees";
import { StaffModalButtons } from "./StaffModalButtons";

const inviteUser = object({
    title: string().optional(),
    firstName: string().required(DefaultErrorMessages.required),
    lastName: string().required(DefaultErrorMessages.required),
    email: string().email("Please enter a valid email").required(DefaultErrorMessages.required)
});

const formSchema: SchemaOf<InviteUserArray> = object({
    users: array().of(inviteUser).min(1, "aa")
});

const defaultInvitee = { firstName: "", lastName: "", email: "" };

enum AssignState {
    ADD,
    ASSIGN,
    NONE
}

const renderExistingUsers = (user: InviteUser): JSX.Element => {
    return (
        <div key={user.email} className="is-flex has-text-overflow-ellipsis">
            <p className="has-text-weight-bold mr-2">{`${user.firstName} ${user.lastName}`}</p>
            <p>{`<${user.email}>`}</p>
        </div>
    );
};

export const AssignStaffUserModal = ({
    locationId,
    role,
    users,
    refresh,
    openClinicalStaffModal
}: AssignStaffUserModalProps): JSX.Element => {
    const validateMethods = useForm<InviteUserArray>({
        resolver: yupResolver(formSchema)
    });

    const { closeModal } = useModal();
    const organization = useAppSelector((x) => x.organization);

    const { batchAssignLocationUser, loading } = useApi();

    const validateSearchMethods = useForm();

    const formSubmitHandler: SubmitHandler<InviteUserArray> = async (data: InviteUserArray) => {
        if (role === RoleId.ClinicalStaff) {
            openClinicalStaffModal?.(data);
            closeModal();
            return;
        }

        try {
            setError(false);

            const parsedUsers = data.users
                .map((user) => {
                    const { id, locations, status, ...parsedUser } = user as any;
                    return parsedUser;
                })
                .filter((x) => !!x);

            await batchAssignLocationUser(organization.id!, locationId!, parsedUsers, role!);

            refresh?.();

            closeModal();
        } catch {
            setError(true);
        }
    };

    const [invitees, setInvitees] = useState<InviteUser[]>([defaultInvitee]);
    const [assignState, setAssignState] = useState<AssignState>(AssignState.NONE);
    const [existingUsers] = useState<InviteUser[]>(
        users.filter(
            (x) =>
                !x.locations
                    ?.find((l) => l?.id === locationId)
                    ?.roles?.map((r) => r?.id)
                    ?.some((lr) => lr === role!)
        )
    );

    const [assignees, setAssignees] = useState<InviteUser[]>([]);

    const [error, setError] = useState(false);

    return (
        <>
            <div>
                {assignState !== AssignState.ASSIGN && (
                    <>
                        <div className="has-text-weight-bold is-size-6">Add new user(s)</div>
                        <div className="mt-2 mb-4">
                            Enter the user’s email address and we’ll send out an email invite.
                        </div>

                        <FormProvider {...validateMethods}>
                            <form
                                onSubmit={validateMethods.handleSubmit(formSubmitHandler)}
                                style={{ height: assignState === AssignState.ADD ? "100%" : "" }}
                                className="is-flex is-flex-direction-column is-justify-content-space-between"
                            >
                                <div>
                                    {invitees.map((_, idx) => {
                                        return (
                                            <Invitees
                                                key={`invitee-${idx}`}
                                                idx={idx}
                                                setAssignState={() => setAssignState(AssignState.ADD)}
                                                showDelete={idx > 0 && idx === invitees.length - 1}
                                                dropInvitee={() => {
                                                    validateMethods.unregister(`users.${idx}`);
                                                    setInvitees(drop(invitees));
                                                }}
                                            />
                                        );
                                    })}
                                    <a
                                        className="has-text-weight-bold is-size-7"
                                        onClick={() => {
                                            setAssignState(AssignState.ADD);
                                            setInvitees(invitees.concat(defaultInvitee));
                                        }}
                                    >
                                        + Add another user
                                    </a>
                                </div>
                                {assignState === AssignState.ADD && (
                                    <StaffModalButtons
                                        confirmText={`Invite ${
                                            invitees.length > 0 ? `${invitees.length} new` : ""
                                        } users`}
                                        disabled={invitees.length === 0}
                                        loading={loading}
                                        error={error}
                                    />
                                )}
                            </form>
                        </FormProvider>
                    </>
                )}
                {assignState === AssignState.NONE && (
                    <div className="divider">
                        <p className="mx-2">OR</p>
                    </div>
                )}
                {assignState !== AssignState.ADD && (
                    <>
                        <div className="has-text-weight-bold is-size-6">Assign to existing user(s)</div>
                        <div className="mt-2 mb-4">Select existing user(s) you would like to assign the role to.</div>

                        <FormProvider {...validateSearchMethods}>
                            <LSearchSelect
                                inputName="searchTable"
                                placeholder="Search by name or email"
                                onSelected={(selected: InviteUser) => {
                                    if (assignState !== AssignState.ASSIGN) {
                                        setAssignState(AssignState.ASSIGN);
                                    }
                                    setAssignees(assignees.concat(selected));
                                }}
                                isRounded
                                selected={assignees.map((x) => x.email)}
                                options={existingUsers}
                                mapFunction={renderExistingUsers}
                            />
                        </FormProvider>

                        {assignees.length > 0 && (
                            <div className="mt-5">
                                <p className="has-text-primary-blue has-text-weight-bold is-size-7 mb-2">
                                    Selected: ({assignees.length})
                                </p>
                                {assignees.map((as, idx) => {
                                    return (
                                        <div key={`assignee-${idx}-${as.email}`} className={styles.selectedUser}>
                                            {`${as.firstName} ${as.lastName} <${as.email}>`}
                                            <button
                                                className="has-background-primary-blue delete"
                                                onClick={() => {
                                                    const deletedAssignee = assignees.filter(
                                                        (x) => x.email !== as.email
                                                    );
                                                    setAssignees(deletedAssignee);
                                                }}
                                            ></button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
            {assignState === AssignState.NONE && <StaffModalButtons confirmText="Invite users" disabled />}
            {assignState === AssignState.ASSIGN && (
                <StaffModalButtons
                    confirmText={`Assign ${assignees.length > 0 ? `to ${assignees.length}` : ""} users`}
                    disabled={assignees.length === 0}
                    loading={loading}
                    onConfirm={() => {
                        void formSubmitHandler({ users: assignees });
                    }}
                    error={error}
                />
            )}
        </>
    );
};

interface AssignStaffUserModalProps {
    locationId?: number;
    role?: RoleId;
    users: InviteUser[];
    refresh?: () => void;
    openClinicalStaffModal?: (users?: InviteUserArray) => void;
}
